// traceLinearOps.js — who reads and writes Linear issues, extracted from the
// scan window's raw request bodies (the same hydration mechanic as the Files
// X-ray: tool_use/tool_result blocks per prompt, deduped across each session's
// cumulative re-sends, aggregated per issue and per actor).
//
// Grounded in real traffic: the Linear MCP server appears under several
// aliases (mcp__linear__*, mcp__linear-server__*, mcp__claude_ai_Linear__*),
// so the tool matcher keys on a server segment containing "linear" rather than
// one fixed prefix. Issue identity rides the human identifier (TEAM-123) in
// `id` / `issueId` arguments; a create (save_issue with no id) is attributed
// from the identifier in its result text. get_issue/save_issue RESULTS are the
// full issue JSON (title, description, project name, status, …) and
// save_comment/list_comments carry comment bodies — so each op also captures
// the actual CONTENT read or written (capped), and issues resolve to their
// PROJECT for the project → issue grouping, both gleaned the same
// labels-not-attribution way. Only MCP Linear tools are parsed — a `linear`
// CLI invocation through Bash is out of scope here, exactly like shell file
// edits are out of scope for the Files tree.

// TEAM-123 — Linear's human issue identifier. Uppercase team key, then digits.
const ISSUE_ID_RE = /^[A-Za-z][A-Za-z0-9]{0,9}-\d{1,6}$/;
const ISSUE_ID_SCAN_RE = /\b([A-Z][A-Z0-9]{0,9}-\d{1,6})\b/;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Per-op captured content (a comment body, an issue description, a read's
// observed description). Linear bodies are prose, not source — a small cap
// keeps a window's scan light while preserving whole comments.
const CONTENT_CAP = 2000;
// A list_comments read captures the first N comments' bodies.
const COMMENT_LIST_CAP = 12;
// Bound structured gleaning over a huge list_issues result.
const GLEAN_CAP = 40;

// Split an MCP tool name into (server, action): "mcp__linear-server__save_issue"
// → ["linear-server", "save_issue"]. Returns null for non-MCP or non-Linear
// names. The action is the LAST "__" segment; the server is everything between
// the "mcp" prefix and the action (a server alias itself may contain "__"
// — claude_ai_Linear does not, but don't assume).
export function parseLinearTool(name) {
  const s = String(name || "");
  const segs = s.split("__");
  if (segs.length < 3 || segs[0] !== "mcp") return null;
  const action = segs[segs.length - 1];
  const server = segs.slice(1, -1).join("__");
  if (!/linear/i.test(server)) return null;
  return { server, action: action.toLowerCase() };
}

// read = observing issues; write = mutating them; browse = reading Linear
// surfaces that aren't one specific issue (lists, projects, teams, users,
// documents). authenticate and unknown verbs are skipped entirely.
export function classifyLinearAction(action, hasIssue) {
  if (/^(save|create|update|delete|archive|unarchive|add|remove)_/.test(action)) return "write";
  if (/^(get|list|search)_/.test(action)) return hasIssue ? "read" : "browse";
  return null;
}

// The issue an op targets, from its arguments: issueId (comments) or id
// (get/save issue). A parentId is a relationship, not the target — a
// save_comment parentId is a comment thread id, a save_issue parentId is a
// different issue — so it never becomes the op's issue. Human identifiers
// normalize to uppercase; a UUID id is kept verbatim (rare — some harnesses
// pass Linear's internal id).
export function issueOfArgs(input) {
  for (const k of ["issueId", "id"]) {
    const v = input?.[k];
    if (typeof v !== "string" || !v) continue;
    if (ISSUE_ID_RE.test(v)) return v.toUpperCase();
    if (UUID_RE.test(v)) return v.toLowerCase();
  }
  return null;
}

function contentText(content) {
  if (typeof content === "string") return content;
  if (Array.isArray(content)) {
    return content.map((p) => (typeof p === "string" ? p : p?.text || "")).join("\n");
  }
  return "";
}

// Same contract as traceFileTree's resultErrored: parseApiRequest normalizes
// the wire's `is_error` to `isError` on every tool_result block, and Claude
// Code surfaces some tool failures only as a <tool_use_error> marker in the
// result content.
function resultErrored(result) {
  if (!result) return false;
  if (result.isError) return true;
  return contentText(result.content).includes("<tool_use_error>");
}

function cap(s) {
  const t = typeof s === "string" ? s : "";
  return t.length > CONTENT_CAP ? t.slice(0, CONTENT_CAP) : t;
}

// cap() plus the truncation FACT — the doc builders below need it to know
// whether an observed description was complete (an incomplete one can't pin
// the document's length, same as a truncated file read).
function capInfo(s) {
  const t = typeof s === "string" ? s : "";
  return { text: t.length > CONTENT_CAP ? t.slice(0, CONTENT_CAP) : t, truncated: t.length > CONTENT_CAP };
}

function str(v) {
  return typeof v === "string" && v ? v : null;
}

// djb2 — stable, tiny; only used for synthetic dedup keys (same as traceFileTree).
function hashString(s) {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

// Glean issue id → { title, project } pairs from a parsed result value — the
// full-issue results carry both, and a list_issues result names many. Labels
// only, never attribution: a list naming 50 issues doesn't mean the agent read
// 50 issues. Bounded walk (one level of arrays under one level of keys — the
// observed shapes: the issue itself, or { issues|comments|projects: [...] }).
function gleanFromResult(d, titles, projects) {
  const take = (o) => {
    const id = str(o?.id);
    if (!id || !ISSUE_ID_RE.test(id)) return;
    const key = id.toUpperCase();
    const title = str(o.title);
    const project = str(o.project);
    if (title && !(key in titles)) titles[key] = title;
    if (project && !(key in projects)) projects[key] = project;
  };
  if (!d || typeof d !== "object") return;
  take(d);
  for (const v of Object.values(d)) {
    if (!Array.isArray(v)) continue;
    for (const item of v.slice(0, GLEAN_CAP)) {
      if (item && typeof item === "object") take(item);
    }
  }
}

// The structured, low-cardinality slice of a write's arguments — what changed
// besides prose (state flips, reassignment, priority, linkage). Kept as an
// ordered small object for chip rendering.
const WRITE_FIELD_KEYS = ["state", "status", "assignee", "priority", "title", "team", "project", "parentId"];
function writeFields(input) {
  const out = {};
  for (const k of WRITE_FIELD_KEYS) {
    const v = input?.[k];
    if (typeof v === "string" && v) out[k === "status" ? "state" : k] = v;
    else if (typeof v === "number") out[k] = String(v);
  }
  if (Array.isArray(input?.labels) && input.labels.length) {
    out.labels = input.labels.filter((l) => typeof l === "string").slice(0, 8).join(", ");
  }
  return out;
}

// A read's observed status slice, from the full-issue result JSON.
const READ_FIELD_KEYS = ["status", "state", "assignee", "priority"];
function readFields(d) {
  const out = {};
  for (const k of READ_FIELD_KEYS) {
    const v = d?.[k];
    if (typeof v === "string" && v) out[k === "status" ? "state" : k] = v;
    else if (typeof v === "number") out[k] = String(v);
  }
  return out;
}

function commentAuthor(a) {
  if (typeof a === "string") return a;
  if (a && typeof a === "object") return str(a.name) || str(a.displayName) || str(a.email) || null;
  return null;
}

// One prompt's turns → its Linear ops. Op shape:
//   { key, action, server, kind: read|write|browse, issue: "TEAM-123"|uuid|null,
//     create: bool, title, project, content, fields, ok, titles, projects }
// `content` is the substantive body the op read or wrote (capped):
//   save_comment → the comment body written; save_issue → the description
//   written; get_issue → the description observed; list_comments → the
//   thread's comment bodies ("author — body" lines). `fields` is the
//   structured delta/observation (state, assignee, priority, …). `titles` /
//   `projects` are id→label maps gleaned from the op's RESULT — labels only,
//   never attribution.
export function extractLinearOps(turns) {
  const ops = [];
  if (!Array.isArray(turns)) return ops;
  const resultById = new Map();
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type === "tool_result" && b.tool_use_id) resultById.set(b.tool_use_id, b);
    }
  }
  for (const turn of turns) {
    for (const b of turn?.blocks || []) {
      if (b?.type !== "tool_use") continue;
      const parsed = parseLinearTool(b.name);
      if (!parsed) continue;
      const input = b.input && typeof b.input === "object" ? b.input : {};
      const result = b.id ? resultById.get(b.id) : null;
      const ok = !resultErrored(result);
      const text = result ? contentText(result.content) : "";
      let resultJson = null;
      if (ok && text && (text[0] === "{" || text[0] === "[")) {
        try { resultJson = JSON.parse(text); } catch { /* text result — fine */ }
      }

      let issue = issueOfArgs(input);
      // A save_issue with no id is a CREATE — the new identifier only exists in
      // the result ("Created TEAM-123 …" or the created issue's JSON).
      const create = parsed.action === "save_issue" && !issue;
      if (create && ok) {
        const rid = str(resultJson?.id);
        if (rid && ISSUE_ID_RE.test(rid)) issue = rid.toUpperCase();
        else {
          const m = ISSUE_ID_SCAN_RE.exec(text);
          if (m) issue = m[1].toUpperCase();
        }
      }
      const kind = classifyLinearAction(parsed.action, Boolean(issue));
      if (!kind) continue;

      const titles = {};
      const projects = {};
      gleanFromResult(resultJson, titles, projects);

      // The op's substantive content + structured fields, by action.
      let content = null;
      let contentTruncated = false;
      let author = null;
      let comments = null;
      let fields = {};
      if (kind === "write") {
        fields = writeFields(input);
        if (parsed.action === "save_comment") {
          const c = capInfo(str(input.body) || "");
          content = c.text || null;
          contentTruncated = c.truncated;
          author = commentAuthor(resultJson?.author);
        } else if (str(input.description)) {
          const c = capInfo(input.description);
          content = c.text;
          contentTruncated = c.truncated;
        }
      } else if (parsed.action === "list_comments" && resultJson) {
        const arr = Array.isArray(resultJson.comments) ? resultJson.comments : [];
        comments = arr.slice(0, COMMENT_LIST_CAP).map((c) => ({
          author: commentAuthor(c?.author),
          body: cap(str(c?.body) || ""),
        })).filter((c) => c.body);
        const lines = comments.map((c) => (c.author ? `${c.author} — ${c.body}` : c.body));
        if (lines.length) content = cap(lines.join("\n\n"));
        // Only mark overflow on real content — with every capped-slice body
        // empty, content is still null and appending would mint "null\n…".
        if (content && arr.length > COMMENT_LIST_CAP) content = `${content}\n…`;
      } else if (kind === "read" && resultJson) {
        const c = capInfo(str(resultJson.description) || "");
        content = c.text || null;
        contentTruncated = c.truncated;
        fields = readFields(resultJson);
      }

      const title = str(input.title) || (issue && titles[issue]) || null;
      const project = str(input.project) || (issue && projects[issue]) || null;

      const key = b.id || `synth:${parsed.action}:${hashString(JSON.stringify(input))}`;
      ops.push({ key, action: parsed.action, server: parsed.server, kind, issue, create, title, project, content, contentTruncated, author, comments, fields, ok, titles, projects });
    }
  }
  return ops;
}

// Flatten per-prompt extractions into the window's real deltas — the same
// mechanic as traceFileTree.collectWindowOps: prompts walked oldest-first, an
// op attributed to the FIRST prompt whose body carries it; later cumulative
// re-sightings in the same session collapse onto it.
export function collectWindowLinearOps(promptEvents) {
  const sorted = [...(promptEvents || [])].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0));
  const seen = new Set();
  const flat = [];
  for (const p of sorted) {
    for (const op of p.ops || []) {
      const dedupKey = `${p.sessionId || ""}|${op.key}`;
      if (seen.has(dedupKey)) continue;
      seen.add(dedupKey);
      flat.push({
        ...op,
        promptId: p.promptId,
        sessionId: p.sessionId || "",
        endpointId: p.endpointId || "",
        endpointName: p.endpointName || "",
        ts: p.ts ?? null,
      });
    }
  }
  return flat;
}

function tsOf(v) {
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return v;
  const p = Date.parse(v);
  return Number.isNaN(p) ? null : p;
}

function newActor(id, name) {
  return { id, name: name || shortId(id), reads: 0, writes: 0, browses: 0, issuesRead: new Set(), issuesWritten: new Set(), sessions: new Set() };
}

function shortId(id) {
  return id ? `${String(id).slice(0, 8)}…` : "unknown";
}

// The window's Linear activity — project-, issue-, and actor-centric:
//   projects: [{ name (null = no project), issues: [issue…], reads, writes }]
//   issues:   [{ id, title, project, reads, writes, created, errored, readers,
//                writers (both [{id, name, n}]), sessions, ops (chronological;
//                each op carries action/kind/content/fields/trace ids) }]
//   actors:   [{ id, name, reads, writes, browses, issuesRead, issuesWritten,
//                sessions }] — an actor is an ENDPOINT (the machine a person's
//                agent runs on); friendly-label resolution happens in the
//                component (useLabels), same as the Files tree.
//   totals:   { issues, projects, reads, writes, browses, created, actors }
export function buildLinearActivity(flatOps) {
  const issues = new Map();
  const actors = new Map();
  const titleOf = new Map();
  const projectOf = new Map();
  let browses = 0;
  let reads = 0;
  let writes = 0;
  let created = 0;

  for (const op of flatOps || []) {
    for (const [id, t] of Object.entries(op.titles || {})) {
      if (!titleOf.has(id)) titleOf.set(id, t);
    }
    for (const [id, p] of Object.entries(op.projects || {})) {
      if (!projectOf.has(id)) projectOf.set(id, p);
    }
    if (op.issue) {
      if (op.title && !titleOf.has(op.issue)) titleOf.set(op.issue, op.title);
      if (op.project && !projectOf.has(op.issue)) projectOf.set(op.issue, op.project);
    }

    let actor = actors.get(op.endpointId);
    if (!actor) {
      actor = newActor(op.endpointId, op.endpointName);
      actors.set(op.endpointId, actor);
    }
    actor.sessions.add(op.sessionId);

    if (op.kind === "browse") {
      if (op.ok) { actor.browses += 1; browses += 1; }
      continue;
    }
    if (!op.issue) continue; // an id-less failed create — nothing to pin it to

    let issue = issues.get(op.issue);
    if (!issue) {
      issue = { id: op.issue, reads: 0, writes: 0, created: 0, errored: 0, readers: new Map(), writers: new Map(), sessions: new Set(), ops: [] };
      issues.set(op.issue, issue);
    }
    issue.ops.push({
      promptId: op.promptId, sessionId: op.sessionId, endpointId: op.endpointId,
      endpointName: op.endpointName, ts: op.ts, action: op.action, kind: op.kind,
      create: op.create, ok: op.ok, content: op.content || null,
      contentTruncated: Boolean(op.contentTruncated), author: op.author || null,
      comments: op.comments || null, fields: op.fields || {},
    });
    if (!op.ok) { issue.errored += 1; continue; }
    issue.sessions.add(op.sessionId);
    const side = op.kind === "write" ? issue.writers : issue.readers;
    const prev = side.get(op.endpointId) || { id: op.endpointId, name: op.endpointName || shortId(op.endpointId), n: 0 };
    prev.n += 1;
    side.set(op.endpointId, prev);
    if (op.kind === "write") {
      issue.writes += 1;
      writes += 1;
      actor.writes += 1;
      actor.issuesWritten.add(op.issue);
      if (op.create) { issue.created += 1; created += 1; }
    } else {
      issue.reads += 1;
      reads += 1;
      actor.reads += 1;
      actor.issuesRead.add(op.issue);
    }
  }

  const issueList = [...issues.values()].map((t) => ({
    ...t,
    title: titleOf.get(t.id) || null,
    project: projectOf.get(t.id) || null,
    readers: [...t.readers.values()].sort((a, b) => b.n - a.n),
    writers: [...t.writers.values()].sort((a, b) => b.n - a.n),
    sessions: t.sessions.size,
    ops: [...t.ops].sort((a, b) => (tsOf(a.ts) ?? 0) - (tsOf(b.ts) ?? 0)),
  }));
  // Most-mutated first — writes are the rarer, higher-signal half — then by
  // total touch volume.
  issueList.sort((a, b) => (b.writes - a.writes) || (b.reads + b.writes) - (a.reads + a.writes) || a.id.localeCompare(b.id));

  // Project → issue grouping. Issues whose project never surfaced in any
  // result group under name:null, LAST — real projects lead.
  const projectGroups = new Map();
  for (const issue of issueList) {
    const key = issue.project || "";
    let g = projectGroups.get(key);
    if (!g) {
      g = { name: issue.project || null, issues: [], reads: 0, writes: 0 };
      projectGroups.set(key, g);
    }
    g.issues.push(issue);
    g.reads += issue.reads;
    g.writes += issue.writes;
  }
  const projectList = [...projectGroups.values()].sort((a, b) => {
    if (!a.name !== !b.name) return a.name ? -1 : 1; // named projects first
    return (b.writes - a.writes) || (b.reads - a.reads) || String(a.name).localeCompare(String(b.name));
  });

  const actorList = [...actors.values()]
    .filter((a) => a.reads + a.writes + a.browses > 0)
    .map((a) => ({
      id: a.id, name: a.name,
      reads: a.reads, writes: a.writes, browses: a.browses,
      issuesRead: a.issuesRead.size, issuesWritten: a.issuesWritten.size,
      sessions: a.sessions.size,
    }));
  actorList.sort((a, b) => (b.writes - a.writes) || (b.reads - a.reads) || a.name.localeCompare(b.name));

  return {
    projects: projectList,
    issues: issueList,
    actors: actorList,
    totals: {
      issues: issueList.length,
      projects: projectList.filter((p) => p.name).length,
      reads, writes, browses, created,
      actors: actorList.length,
    },
  };
}

// ── Documents for the Files-style renderer ──────────────────────────────────
// An issue's DESCRIPTION behaves exactly like a small file: save_issue writes
// the whole body, get_issue observes it. Mapping ops onto traceFileTree's op
// shape lets reconstructFile/AnnotatedFileBody replay it unchanged — per-line
// versions across rewrites, and a read that disagrees with the replayed state
// is a real "changed off-tool" signal (the issue was edited in Linear's web
// UI between agent touches).
export function issueDescDoc(issueOps) {
  const ops = [];
  for (const op of issueOps || []) {
    if (!op.content) continue;
    if (op.kind === "write" && op.action === "save_issue") {
      ops.push({
        kind: "write", ok: op.ok, content: op.content,
        contentTruncated: Boolean(op.contentTruncated),
        promptId: op.promptId, sessionId: op.sessionId, ts: op.ts,
      });
    } else if (op.kind === "read" && op.action === "get_issue") {
      const lines = op.content.split("\n");
      ops.push({
        kind: "read", ok: op.ok,
        obs: lines.map((t, i) => [i + 1, t]),
        // A capped description can't pin the doc's length — same contract as
        // a truncated file read.
        obsFull: !op.contentTruncated,
        readLines: lines.length,
        promptId: op.promptId, sessionId: op.sessionId, ts: op.ts,
      });
    }
  }
  return ops;
}

// Comments as one-version mini-documents for the same renderer: a
// save_comment is a whole-body WRITE (its lines render as written
// in-window), a comment surfaced by list_comments is a READ observation.
// Deduped by body — a comment both written and later re-observed keeps its
// write provenance (authored-in-window is the interesting fact). Returns
// [{ author, written, op }] chronological; feed each `op` to
// reconstructFile([op]).
export function issueCommentDocs(issueOps) {
  const byBody = new Map();
  for (const op of issueOps || []) {
    if (op.action === "save_comment" && op.content) {
      const key = op.content.trim();
      if (!key) continue;
      byBody.set(key, {
        author: op.author || null,
        written: true,
        op: {
          kind: "write", ok: op.ok, content: op.content,
          contentTruncated: Boolean(op.contentTruncated),
          promptId: op.promptId, sessionId: op.sessionId, ts: op.ts,
        },
      });
    } else if (op.action === "list_comments") {
      for (const c of op.comments || []) {
        const key = String(c.body || "").trim();
        if (!key || byBody.has(key)) continue;
        const lines = c.body.split("\n");
        byBody.set(key, {
          author: c.author || null,
          written: false,
          op: {
            kind: "read", ok: op.ok,
            obs: lines.map((t, i) => [i + 1, t]),
            obsFull: true, readLines: lines.length,
            promptId: op.promptId, sessionId: op.sessionId, ts: op.ts,
          },
        });
      }
    }
  }
  return [...byBody.values()].sort((a, b) => (tsOf(a.op.ts) ?? 0) - (tsOf(b.op.ts) ?? 0));
}
